import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { UncontrolledPopover } from 'reactstrap'
import PropTypes from 'prop-types'

import classes from './RegistrationActions.module.scss'

import { ReactComponent as EllipsisIcon } from '../../../../assets/images/common/icons/ellipsis-icon.svg'
import * as enrollmentActions from '../../../../actions/enrollmentActions'
import { forceSeriesRefresh } from '../../../../reducers/seriesReducer'
import RefundEnrollmentModal from './modals/RefundEnrollmentModal'
import DropEnrollmentModal from './modals/DropEnrollmentModal'
import ApproveEnrollmentModal from './modals/ApproveEnrollmentModal'
import SubmitButton from '../../../common/buttons/SubmitButton'
import CancelButton from '../../../common/buttons/CancelButton'
import classnames from 'classnames'

const RegistrationActions = ({ course, enrollment }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const paidOnline = enrollment.paymentStatus === 'paid_online'
  const [actionLoader, setActionLoader] = useState(false)
  const [coachMessage, setCoachMessage] = useState('')
  const [showRefundEnrollmentModal, setShowRefundEnrollmentModal] = useState(false)
  const [showDropEnrollmentModal, setShowDropEnrollmentModal] = useState(false)
  const [showApproveEnrollmentModal, setShowApproveEnrollmentModal] = useState(false)
  const [notifyViaEmail, setNotifyViaEmail] = useState(true)

  const handleCoachMessageChange = e => {
    setCoachMessage(e.target.value)
  }

  const handleApproveEnrollment = () => {
    setActionLoader(true)
    dispatch(enrollmentActions.updateEnrollment({ enrollmentData: { id: enrollment.id, status: 'active' }, coachMessage })).unwrap()
      .catch(() => toast.error(t('common.somethingWentWrong'), { position: toast.POSITION.TOP_RIGHT }))
      .finally(() => {
        setActionLoader(false)
        setShowApproveEnrollmentModal(false)
        setCoachMessage('')
      })
  }

  const handleRemoveEnrollment = (refund) => {
    setActionLoader(true)
    dispatch(enrollmentActions.deleteEnrollment({ enrollmentId: enrollment.id, refund, coachMessage, notifyViaEmail })).unwrap()
      .then(() =>
        toast.success(
          enrollment.status === 'active'
            ? t('programs.manage.registrationActions.enrollmentDropped')
            : t('programs.manage.registrationActions.enrollmentDeclined'),
          { position: toast.POSITION.TOP_RIGHT }
        )
      )
      .catch(() => toast.error(t('common.somethingWentWrong'), { position: toast.POSITION.TOP_RIGHT }))
      .finally(() => {
        setActionLoader(false)
        setCoachMessage('')
        setNotifyViaEmail(true)
        setShowDropEnrollmentModal(false)
        setShowRefundEnrollmentModal(false)
        dispatch(forceSeriesRefresh())
      })
  }

  return (
    <div id='RegistrationActions' className='d-flex justify-content-center align-items-center'>
      {enrollment.status === 'applied' ? (
        <>
          <SubmitButton
            noTopMargin
            type='button'
            buttonMessage={t('programs.manage.registrationActions.approve')}
            onClick={() => setShowApproveEnrollmentModal(true)}
            disabled={actionLoader}
          />

          <CancelButton
            buttonMessage={t('programs.manage.registrationActions.decline')}
            onClick={() => setShowDropEnrollmentModal(true)}
            disabled={actionLoader}
          />
        </>
      ) : (
        <>
          <button
            id={`ellipsis-button-${enrollment.id}`}
            className={classnames('w-100 d-flex justify-content-center align-items-center', classes.ellipsisButton)}
          >
            <EllipsisIcon className={classes.icon} />
          </button>
          {!actionLoader && !showDropEnrollmentModal && !showRefundEnrollmentModal &&
            <UncontrolledPopover
              placement='bottom'
              target={`ellipsis-button-${enrollment.id}`}
              trigger='legacy'
              popperClassName='popover-style'
            >
              <div className={classnames('d-flex flex-column', classes.actions)}>
                <Link to={`/profile/${enrollment.user.id}`}>{t('programs.manage.registrationActions.seeProfile')}</Link>
                {!course.isCompleted &&
                  <button
                    onClick={() => paidOnline ? setShowRefundEnrollmentModal(true) : setShowDropEnrollmentModal(true)}
                    aria-hidden='true'
                  >
                    {t('programs.manage.registrationActions.dropEnrollment')}
                  </button>
                }
              </div>
            </UncontrolledPopover>
          }
        </>
      )}
      {showRefundEnrollmentModal && (
        <RefundEnrollmentModal
          notifyViaEmail={notifyViaEmail}
          handleCheckboxChange={() => setNotifyViaEmail(notify => !notify)}
          showModal={showRefundEnrollmentModal}
          closeModal={() => { setShowRefundEnrollmentModal(false); setCoachMessage(''); setNotifyViaEmail(true) }}
          coachMessage={coachMessage}
          handleCoachMessageChange={handleCoachMessageChange}
          handleRemoveEnrollment={handleRemoveEnrollment}
          enrollment={enrollment}
          isLoading={actionLoader}
        />
      )}
      {showDropEnrollmentModal && (
        <DropEnrollmentModal
          notifyViaEmail={notifyViaEmail}
          handleCheckboxChange={() => setNotifyViaEmail(notify => !notify)}
          showModal={showDropEnrollmentModal}
          closeModal={() => { setShowDropEnrollmentModal(false); setCoachMessage(''); setNotifyViaEmail(true) }}
          coachMessage={coachMessage}
          handleCoachMessageChange={handleCoachMessageChange}
          handleRemoveEnrollment={() => handleRemoveEnrollment(false)}
          enrollment={enrollment}
          isLoading={actionLoader}
        />
      )}
      {showApproveEnrollmentModal && (
        <ApproveEnrollmentModal
          showModal={showApproveEnrollmentModal}
          closeModal={() => { setShowApproveEnrollmentModal(false); setCoachMessage('') }}
          coachMessage={coachMessage}
          handleCoachMessageChange={handleCoachMessageChange}
          handleApproveEnrollment={handleApproveEnrollment}
          isLoading={actionLoader}
        />
      )}
    </div>
  )
}

RegistrationActions.propTypes = {
  course: PropTypes.shape({
    id: PropTypes.number,
    isCompleted: PropTypes.bool,
  }),
  enrollment: PropTypes.shape({
    id: PropTypes.number,
    paymentStatus: PropTypes.oneOf(['paid', 'invoiced', 'outstanding', 'paid_online']),
    status: PropTypes.oneOf(['applied', 'active', 'suspended', 'completed', 'pending']),
    user: PropTypes.shape({
      id: PropTypes.number,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
    }),
  }),
}

export default RegistrationActions
